/* global rwgps */
import React, { Fragment } from 'react'
import styles from './TRouteAtAGlance.scss'
import { featureTypes } from 'common/MapV2/featureTypes'
import { Distance, Elevation, Duration, Grade } from 'common/units'
import { Icon } from 'common/Icon'
import { TriggerUpsellModal } from 'common/TriggerUpsellModal'
import { useFormatMessage, commonMetrics } from 'lib/localization'
import _ from 'lodash'
import cx from 'classnames'
import p from 'prop-types'

const pointMetric = ({ troute, index, label }) => ({
  type: featureTypes.POINT_METRIC,
  data: {
    highlightPoint: {
      trackPoint: troute.trackPoints[index],
      index
    },
    label
  }
})

export const TRouteAtAGlance = ({ troute, objectType, onFeatureHovered }) => {
  const fm = useFormatMessage()
  const maxGradeStr = fm(commonMetrics.maxGrade, 'Max Grade')
  const minGradeStr = fm(commonMetrics.minGrade, 'Min Grade')

  return (
    <div className={styles.TRouteAtAGlance}>
      <Distance value={troute.distance}>
        {m => <Metric metric={m} label={fm(commonMetrics.distance, 'Distance')} i='distance' />}
      </Distance>
      <Elevation value={troute.elevationGain}>
        {m => <Metric metric={m} label={_.startCase(fm(commonMetrics.elevationGain, 'Elevation Gain'))} i='md-add' />}
      </Elevation>
      <Grade value={troute.metrics?.grade.max}>
        {m => <Metric
          metric={m}
          label={maxGradeStr}
          i='grade-up'
          onMouseEnter={() => onFeatureHovered(pointMetric({
            troute,
            index: troute.metrics?.grade.max_i, // eslint-disable-line
            label: `${maxGradeStr}: ${m.valueToString()}%`
          }))}
          onMouseLeave={() => onFeatureHovered()} />}
      </Grade>
      {objectType === 'trip'
        ? <Duration bigUnits value={troute.movingTime}>
          {m => <Metric metric={m} label={fm(commonMetrics.movingTime, 'Moving Time')} unitLabel='hrs' i='md-stopwatch' />}
        </Duration>
        : (_.get(troute.permissions, 'navigate') ? <EstimatedTime r={troute} /> : <UpsellEstimatedTime />)}
      <Elevation value={troute.elevationLoss}>
        {m => <Metric metric={m} label={_.startCase(fm(commonMetrics.elevationLoss, 'Elevation Loss'))} i='md-remove' />}
      </Elevation>
      <Grade value={troute.metrics?.grade.min}>
        {m => <Metric
          metric={m}
          label={minGradeStr}
          i='grade-down'
          onMouseEnter={() => onFeatureHovered(pointMetric({
            troute,
            index: troute.metrics?.grade.min_i, // eslint-disable-line
            label: `${minGradeStr}: ${m.valueToString()}%`
          }))}
          onMouseLeave={() => onFeatureHovered()} />}
      </Grade>
    </div>
  )
}

TRouteAtAGlance.propTypes = {
  troute: p.object.isRequired,
  objectType: p.string.isRequired,
  onFeatureHovered: p.func.isRequired
}

const Metric = ({ metric, noUnits, label, unitLabel, i, className, tip, ...props }) => (
  <div className={cx(styles.Metric, className)} {...props} data-tip title={tip || label}>
    <Icon className={styles.icon} i={i} />
    <span className={styles.metricValue}>
      {metric.valueToString()}{!noUnits && <span className={styles.units}>{unitLabel || metric.short}</span>}
    </span>
  </div>
)

class EstimatedTime extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      mcResult: null
    }
  }

  componentDidMount () {
    this.computeMetrics()
  }

  componentDidUpdate () {
    if (!this.state.mcResult) {
      this.computeMetrics()
    }
  }

  computeMetrics = () => {
    const { r } = this.props
    if (!r.get('rawPoints')) { return }
    // keeping this around so that we get lots of warning if something causes
    // this (comparatively expensive) re-render
    console.log(`⚠️ calculated estimated time`)
    const tpsResult = rwgps.TrackPoint.fromJSON(r.get('rawPoints'))
    const points = tpsResult.trackPoints
    const data = new rwgps.Data(points)

    // in our case, setting readonly to false can't cause problems here because
    // the source data is immutable
    const mc = new rwgps.MetricsCalculator(data, false)
    const mcResult = mc.calculate()
    this.setState({ mcResult })
  }

  render () {
    const { mcResult } = this.state
    // the presence of a mcResult, but a missing/NaN estimatedTime implies that
    // we don't have enough data to calculate estimated time.
    if (!mcResult || !mcResult.estimatedTime) {
      return <Duration value={0} bigUnits>
        {m => <Metric
          noUnits
          className={styles.pendingMetric}
          metric={m}
          label='estimated time'
          i='md-time'
          tip={mcResult ? "We don't have enough ride data to calculate your estimated time" : 'Calculating Estimated Time...'} />}
      </Duration>
    }
    return (
      <Duration value={mcResult.estimatedTime} bigUnits>
        {m => <Metric noUnits metric={m} label={`estimated time (${m.short})`} i='md-time' />}
      </Duration>
    )
  }
}

const UpsellEstimatedTime = () => (
  <TriggerUpsellModal className={styles.Metric} feature={'estimatedTime'}>
    <Icon className={styles.icon} i={'md-time'} title={'estimate time'} />
    <span className={styles.metricValue}>
      <Duration value={0}>
        {m => <Fragment>
          {m.valueToString()}
        </Fragment>}
      </Duration>
    </span>
    <span className={styles.premiumRequired}>
      <Icon i={'premium-arrow'} />
    </span>
  </TriggerUpsellModal>
)
